import type { Block } from "@cos/shared";

/**
 * An image on a message. `url` is resolved once, when the message is built:
 * for a sent message it is a local object URL (so the shopper's own photo
 * appears the instant they send it, not after a round trip), and for one
 * that arrived from the server it points at the authorised fetch route.
 */
export interface WidgetAttachment {
  id: string;
  url: string;
  width?: number;
  height?: number;
}

export interface WidgetMessage {
  id: string;
  role: "user" | "assistant" | "agent" | "system";
  text: string;
  blocks: Block[] | null;
  attachments?: WidgetAttachment[];
  seq?: number;
  /** Still streaming — no `seq` yet, see PLAN.md's note on `msgDeltaSchema`. */
  pending?: boolean;
}

export type ConnectionStatus = "connecting" | "open" | "reconnecting" | "closed";
export type ConversationStatus = "active" | "escalated" | "closed";
export type ConversationControl = "bot" | "human";

export interface WidgetState {
  connection: ConnectionStatus;
  panelOpen: boolean;
  botName: string;
  greeting?: string;
  /** Whether to offer the attach button — a capability from `session.ready`, not the gate (the API re-checks). */
  allowImageUploads: boolean;
  conversationStatus: ConversationStatus;
  control: ConversationControl;
  messages: WidgetMessage[];
  lastSeq: number;
  botTyping: boolean;
  /** An agent is typing — distinct from `botTyping`, which the widget infers from its own send. */
  agentTyping: boolean;
  /**
   * The launcher is a state machine: `idle | unread(n) | typing |
   * agent-online` (PLAN.md "Attention & unread"). Unread survives page
   * navigation, which is the whole point — an agent's reply has to land
   * when the shopper has closed the widget and wandered off.
   */
  unreadCount: number;
  /** Previews the newest unread message next to the closed launcher. */
  teaser?: string;
  /** The "notify me when someone replies" card: offered, or waiting on the browser's own dialog. Absent means not shown. */
  pushPrompt?: "offer" | "busy";
}

export const initialWidgetState: WidgetState = {
  connection: "connecting",
  panelOpen: false,
  botName: "",
  allowImageUploads: false,
  conversationStatus: "active",
  control: "bot",
  messages: [],
  lastSeq: 0,
  botTyping: false,
  agentTyping: false,
  unreadCount: 0,
};

type Listener = () => void;

/**
 * A plain external store rather than Preact context: the WS client (outside
 * the component tree) needs to push updates in, and `useWidgetState` below
 * is the one place components read them back out.
 */
export class Store {
  #state: WidgetState;
  readonly #listeners = new Set<Listener>();

  constructor(initial: WidgetState) {
    this.#state = initial;
  }

  get state(): WidgetState {
    return this.#state;
  }

  set(patch: Partial<WidgetState>): void {
    this.#state = { ...this.#state, ...patch };
    this.#emit();
  }

  update(fn: (state: WidgetState) => WidgetState): void {
    this.#state = fn(this.#state);
    this.#emit();
  }

  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #emit(): void {
    for (const listener of this.#listeners) listener();
  }
}
